import type { TFunction } from "i18next"

import type { Tier } from "@/lib/badge-tiers"
import type { BadgeMedallionAward, BadgeMedallionRow } from "@/types/badges"

/**
 * OKS-969 — pure view-model + label rules for the badge medallion strip.
 *
 * No React, no `t()` calls of its own (the caller's `t` is passed in), and
 * **no sorting**: rows arrive from the server already ordered (tier desc →
 * newest-award-in-family desc → family identifier asc) and neither this module
 * nor the strip may re-order them. `Brand/Crew/Show` and OKS-970's roster both
 * import from here, so a rule that lived inside a component would have to be
 * duplicated or lifted the moment the second consumer landed.
 *
 * Why the label rules live here rather than inside `<BadgeMedallion>`: the
 * component composes no user-facing copy at all (the OKS-937 rule — a component
 * that composes copy without i18n produces untranslated strings *by
 * construction*, which is how a hardcoded English `— locked` survived three
 * tickets in a French-default product). Keeping the rule outside the component
 * gives both properties at once: the component stays hermetic, and the rule
 * still has exactly one home.
 */

/**
 * Rendering-ready shape for one medallion.
 *
 * Carries everything the label helpers need (`kind`, `i18nKey`, `name`) so a
 * caller threads ONE object rather than a view model plus the wire row it came
 * from — the strip's `labels` adapters would otherwise have to keep both in
 * scope and hand them over in matching pairs.
 */
export interface MedallionViewModel {
  /** Stable React key — the family identifier, unique within a strip. */
  key: string
  identifier: string
  kind: "milestone" | "custom"
  /** Milestones only; `null` on customs. */
  i18nKey: string | null
  /** Customs only (brand-authored); `null` on milestones. */
  name: string | null
  /** From the wire; NOT re-derived via `tierFor()`. See `BadgeMedallionRow`. */
  tier: Tier
  icon: string
  accentColor: string
  imageUrl: string | null
  /**
   * Which display primitive to render. Lifted verbatim from `<BadgeTile>`:
   * custom badges always render the frame-less `<BadgeImage>`; a milestone
   * renders it only when the brand has an artwork override, otherwise it keeps
   * the shipped `<PremiumBadge>` medallion. Same rule, same branch, so the
   * strip and the creator goal board cannot disagree about a given badge.
   */
  showImage: boolean
  /** `awards.length`; greater than 1 only on the two expanding families. */
  count: number
  /** Newest first, exactly as handed over. */
  awards: BadgeMedallionAward[]
}

/** Maps wire rows to view models, preserving server order. */
export function toMedallionViewModels(
  rows: BadgeMedallionRow[],
): MedallionViewModel[] {
  return rows.map((row) => ({
    key: row.identifier,
    identifier: row.identifier,
    kind: row.kind,
    i18nKey: row.i18n_key,
    name: row.name,
    tier: row.tier,
    icon: row.icon,
    accentColor: row.accent_color,
    imageUrl: row.image_url,
    showImage: row.kind === "custom" || row.image_url != null,
    count: row.count,
    awards: row.awards,
  }))
}

/**
 * The medallion's family name.
 *
 * Milestones resolve `badges.milestones.{i18n_key}.name`; customs use the
 * brand-authored `name`. Deliberately the BARE family name even for a repeated
 * family: the per-win disambiguation (month, mission) belongs to the popover
 * lines, because a medallion labelled "Meilleur créateur — septembre 2026"
 * would be naming one win while the disc represents three.
 */
export function medallionName(vm: MedallionViewModel, t: TFunction): string {
  if (vm.kind === "milestone" && vm.i18nKey) {
    return t(`badges.milestones.${vm.i18nKey}.name`)
  }
  return vm.name ?? ""
}

/**
 * The popover's per-win lines, newest first — one per underlying award.
 *
 * `top_performer` wins name their month, `first_publisher` wins name their
 * mission, and every other family (which cannot repeat) yields a single line
 * carrying the bare family name. A win whose disambiguator is absent degrades
 * to that same bare name for THAT line only: a PRESENCE test, not a nil test,
 * so a blank mission title falls back rather than rendering a dangling
 * separator.
 */
export function medallionAwardLines(
  vm: MedallionViewModel,
  t: TFunction,
  locale: string,
): string[] {
  const fallback = medallionName(vm, t)

  return vm.awards.map((award) => {
    if (vm.i18nKey === "top_performer" && award.month) {
      const monthName = new Date(
        award.month.year,
        award.month.month - 1,
        1,
      ).toLocaleString(locale, { month: "long", year: "numeric" })
      return t("badges.milestones.top_performer.name_with_month", {
        month: monthName,
      })
    }

    if (vm.i18nKey === "first_publisher" && award.mission?.title?.trim()) {
      return t("badges.milestones.first_publisher.name_with_mission", {
        mission: award.mission.title,
      })
    }

    return fallback
  })
}

/** Localised "Obtenu le {date}" for one award. */
export function medallionEarnedOn(
  award: BadgeMedallionAward,
  t: TFunction,
  locale: string,
): string {
  const date = new Date(award.earned_at).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
  return t("badges.medallions.earned_on", { date })
}

/**
 * OKS-975 — the popover's complete content for one medallion: title + lines.
 *
 * # Why this is a function and not two lines at each call site
 *
 * It was two lines at each call site, and they diverged. `Brand/Crew/Show` and
 * OKS-970's roster each carried a byte-similar inline adapter; OKS-970's F2
 * fixed a real difference between them (the roster called
 * `medallionAwardLines` unconditionally, which for a single-award family
 * returns the BARE FAMILY NAME — so the popover rendered its own title twice,
 * and differently from the same badge on the brand page). The fix made the two
 * copies agree. Nothing kept them agreeing: one of them is unreachable
 * (`interactive={false}`), so the compiler, the tests and the browser were all
 * equally blind to the next divergence. Two copies of a rule where one cannot
 * be observed is not duplication, it is a latent fork.
 *
 * # The rule
 *
 * A repeated family enumerates its individual wins — each month, each mission —
 * because that disambiguation is exactly what the nameless medallion declines
 * to carry. A single-award family instead shows WHEN it was earned: running it
 * through the per-win enumeration would yield the bare family name, which the
 * popover has already used as its title, and a reveal that restates its own
 * heading tells the reader nothing.
 *
 * ⚠️ The boundary is `count > 1`, not `count >= 1`. At `>= 1` every family
 * takes the enumeration branch and single-award popovers regress to the F2
 * "title twice" shape — which is a live mutant in LQE's matrix, not a
 * hypothetical.
 */
export function medallionPopover(
  vm: MedallionViewModel,
  t: TFunction,
  locale: string,
): { title: string; lines: string[] } {
  return {
    title: medallionName(vm, t),
    lines:
      vm.count > 1
        ? medallionAwardLines(vm, t, locale)
        : vm.awards.map((award) => medallionEarnedOn(award, t, locale)),
  }
}
